import { useState } from "react";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { StaffBookingSummaryResponse } from "@repo/api-client";
import { useVenue } from "../../shell/VenueContext";
import { StatusBadge } from "../Badges";
import { DeclineDialog } from "../DeclineDialog";
import { bookingWhen, guestName, isNum } from "../format";
import { useApproveRequest } from "../mutations";
import { flattenPages, useInbox } from "../queries";
import { bookingRejectionOf } from "../rejections";

/**
 * Request inbox: the PENDING bookings, read with a distinct review treatment (never the confirmed
 * colour), with the approve/decline MUTATIONS mounted per row (request-to-book). Approve re-passes the
 * atomic claim server-side (R6); on a capacity conflict the row shows the reassign-or-decline warning
 * and stays PENDING. Decline opens a dialog (optional reason + a live-checked proposed alternative). A
 * decision's `booking.*` SSE event drops the row on the next invalidation.
 */
export function InboxView({ onSelect }: { onSelect: (b: StaffBookingSummaryResponse) => void }) {
  const { t } = useTranslation();
  const query = useInbox(useVenue().venueId);
  const rows = flattenPages(query.data);

  if (query.isLoading) {
    return (
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("bookings.list.loading")}</p>
    );
  }
  if (rows.length === 0) {
    return (
      <p role="note" className={css({ fontSize: "sm", color: "fg.muted" })}>
        {t("bookings.inbox.empty")}
      </p>
    );
  }

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("bookings.inbox.readonly")}</p>
      <ul className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
        {rows.map((b) => (
          <InboxRow key={b.bookingId} booking={b} onSelect={onSelect} />
        ))}
      </ul>
      {query.hasNextPage ? (
        <button
          type="button"
          className={css({
            alignSelf: "center",
            px: "4",
            py: "2",
            rounded: "md",
            borderWidth: "1px",
            fontSize: "sm",
          })}
          onClick={() => void query.fetchNextPage()}
        >
          {t("bookings.list.loadMore")}
        </button>
      ) : null}
    </div>
  );
}

/**
 * One request row: guest snapshot + countdown, plus the approve/decline controls. Each row owns its own
 * approve mutation + decline dialog so the state is local; a 409 typed rejection on approve surfaces the
 * inline conflict warning without dropping the row (the request stays PENDING — R6).
 */
function InboxRow({
  booking,
  onSelect,
}: {
  booking: StaffBookingSummaryResponse;
  onSelect: (b: StaffBookingSummaryResponse) => void;
}) {
  const { venue, venueId } = useVenue();
  const { t, i18n } = useTranslation();
  const approve = useApproveRequest(venueId, booking.bookingId ?? "");
  const [declineOpen, setDeclineOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decided = booking.pendingDecision !== "PENDING";

  function onApprove() {
    setError(null);
    approve.mutate(
      { version: booking.version ?? 0 },
      {
        onError: (err) => {
          const rej = bookingRejectionOf(err);
          setError(rej ? t("bookings.inbox.conflict") : t("bookings.error.approveFailed"));
        },
      },
    );
  }

  return (
    <li
      className={css({
        borderWidth: "1px",
        rounded: "md",
        p: "3",
        bg: "booking.review.bg",
        borderColor: "booking.review.border",
        display: "flex",
        flexWrap: "wrap",
        gap: "3",
        alignItems: "center",
        justifyContent: "space-between",
      })}
    >
      <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
        <button
          type="button"
          className={css({
            fontWeight: "semibold",
            textAlign: "left",
            color: "booking.review.fg",
            textDecoration: "underline",
          })}
          onClick={() => onSelect(booking)}
        >
          {guestName(booking, t("bookings.noName"))}
        </button>
        <span className={css({ fontSize: "sm", color: "fg.muted" })}>
          {isNum(booking.startSec)
            ? bookingWhen(booking.startSec, venue.timeZone, i18n.language)
            : "—"}{" "}
          · {t("bookings.field.party")}: {booking.partySize ?? "—"}
        </span>
        {error ? (
          <span role="alert" className={css({ fontSize: "xs", color: "booking.noShow.fg" })}>
            {error}
          </span>
        ) : null}
      </div>
      <div className={css({ display: "flex", alignItems: "center", gap: "2", flexWrap: "wrap" })}>
        {isNum(booking.pendingHoldExpiresAtSec) ? (
          <span className={css({ fontSize: "xs", color: "booking.review.fg" })}>
            {t("bookings.inbox.holdUntil", {
              time: bookingWhen(booking.pendingHoldExpiresAtSec, venue.timeZone, i18n.language),
            })}
          </span>
        ) : null}
        <StatusBadge status={booking.status} />
        {decided ? null : (
          <>
            <button
              type="button"
              className={approveButton}
              disabled={approve.isPending}
              onClick={onApprove}
            >
              {approve.isPending ? t("bookings.inbox.approving") : t("bookings.inbox.approve")}
            </button>
            <button type="button" className={declineButton} onClick={() => setDeclineOpen(true)}>
              {t("bookings.inbox.decline")}
            </button>
          </>
        )}
      </div>

      {declineOpen ? (
        <DeclineDialog
          booking={booking}
          onClose={() => setDeclineOpen(false)}
          onDeclined={() => setDeclineOpen(false)}
        />
      ) : null}
    </li>
  );
}

const approveButton = css({
  px: "3",
  py: "1.5",
  rounded: "md",
  fontSize: "sm",
  fontWeight: "medium",
  bg: "colorPalette.default",
  color: "colorPalette.fg",
  _hover: { bg: "colorPalette.emphasized" },
  _disabled: { opacity: "0.6", cursor: "not-allowed" },
});

const declineButton = css({
  px: "3",
  py: "1.5",
  rounded: "md",
  fontSize: "sm",
  fontWeight: "medium",
  borderWidth: "1px",
  borderColor: "border.default",
  bg: "bg.default",
  _hover: { bg: "bg.subtle" },
});
